/**
 * Shopify App Bridge v3 singleton.
 *
 * Initialised once at module load — reads `host` and `shop` from the URL
 * that Shopify injects when opening the embedded app. Outside the Shopify
 * Admin (e.g. local dev with DEV_BYPASS_AUTH) the instance stays null and
 * callers fall back to the sessionStorage stub set by the dev server.
 *
 * Usage:
 *   import { fetchSessionToken } from "@/lib/app-bridge";
 *   const token = await fetchSessionToken(); // always resolves, never throws
 */

import { createApp } from "@shopify/app-bridge";
import { getSessionToken } from "@shopify/app-bridge/utilities";
import type { ClientApplication } from "@shopify/app-bridge";

const params = new URLSearchParams(window.location.search);
const host = params.get("host") ?? "";
const apiKey = (import.meta.env.VITE_SHOPIFY_API_KEY as string | undefined) ?? "";

// Only initialise when running inside the Shopify Admin iframe
export let shopifyApp: ClientApplication | null = null;

if (host && apiKey) {
  try {
    shopifyApp = createApp({
      apiKey,
      host,
      forceRedirect: true,
    });
  } catch (err) {
    console.warn("[app-bridge] Init failed (expected in local dev):", err);
  }
}

/**
 * Returns a fresh Shopify session token (JWT) for use as a Bearer token.
 * Returns null when running outside the Shopify Admin or in local dev mode.
 */
export async function fetchSessionToken(): Promise<string | null> {
  if (!shopifyApp) return null;
  try {
    return await getSessionToken(shopifyApp);
  } catch {
    return null;
  }
}
